import { ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { DialogService } from '@adgate/common/services/dialog-service';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { BaseObject } from '@adgate/common/types/base-object.type';
import { ComparatorService } from '@adgate/common/services/comparator-service';
import { AdGateConstants } from '@adgate/common/constants/adgate.constants';
import { FileSelector } from '@adgate/common/components/file-selector/file-selector';
import { AsperaFileSelector } from '@adgate/common/components/aspera-file-selector/aspera-file-selector';
import { CustomValidators } from '@adgate/common/validators/custom-validators.validator';
import { Campaign } from '@adgate/modules/streaming/types/campaign.type';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { forkJoin } from 'rxjs';
import { CommonObjectService } from '@adgate/common/services/common-object-service';
import { CommercialType } from '@adgate/common/types/commercial-type.type';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { Privilege } from '@adgate/modules/admin/types/privilege.type';
import { User } from '@adgate/modules/admin/types/user.type';
import { ChannelFamily } from '@adgate/common/types/channel-family.type';
import { Creativity } from '@adgate/modules/streaming/types/creativity.type';
import { CreateCreativityRequest } from '@adgate/modules/streaming/types/create-creativity-request.type';
import { StringUtils } from '@adgate/common/components/helpers/string-utils.helper';

@Component({
  selector: 'app-streaming-creativity-add-dialog-component',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatTableModule,
    MatTooltip,
    ObjectDescPipe,
    FileSelector,
    AsperaFileSelector
  ],
  templateUrl: './streaming-creativity-add-dialog-component.html',
  styleUrl: './streaming-creativity-add-dialog-component.scss',
})
export class StreamingCreativityAddDialogComponent implements OnInit
{
  @ViewChild('multiFileSelectorHttp') multiFileSelectorHttp !: FileSelector;
  @ViewChild('multiFileSelectorAspera') multiFileSelectorAspera !: AsperaFileSelector;

  private dialogRef: MatDialogRef<StreamingCreativityAddDialogComponent> = inject(MatDialogRef<StreamingCreativityAddDialogComponent>);
  private fb: FormBuilder = inject(FormBuilder);
  private dialogService: DialogService = inject(DialogService);
  public comparatorService: ComparatorService = inject(ComparatorService);
  public commonObjectService: CommonObjectService = inject(CommonObjectService);
  public authService: AuthenticationService = inject(AuthenticationService);
  public campaign: Campaign = inject(MAT_DIALOG_DATA);

  creativityForm: FormGroup;
  displayedColumns: string[] = ['vertical', 'format', 'countryRegion', 'placementName', 'filename', 'actions'];

  tableDataSource: any[] = [];

  agencies: BaseObject[] = [];
  channelFamilies: BaseObject[] = [];
  countries: BaseObject[] = [];
  formats: CommercialType[] = [];

  showAddFilesButton: boolean = false;
  showHttpFileSelectors: boolean = false;
  showAsperaFileSelectors: boolean = false;
  uploadMethods: BaseObject[] = [
    { id: 0, description: "Upload Later" },
    { id: 1, description: "HTTP" },
    { id: 2, description: "Aspera Connect" }
  ];

  constructor()
  {
    this.creativityForm = this.fb.group({
      uploadMethod: ['', Validators.required],
      file: [{value: '', disabled: true}],
      rows: this.fb.array([]) // FormArray for table rows
    });
  }

  ngOnInit(): void
  {
    let officeId: number | null = null;

    if (!this.authService.checkPrivilege([Privilege.STRM_UPLOADCREATIVITY])) {
      this.creativityForm.get('uploadMethod')?.setValue(this.uploadMethods[0]);
      this.creativityForm.get('uploadMethod')?.disable();
    }

    let userAttributes = this.authService.getUserAttributes();
    if (userAttributes.type === User.TYPE_OFFICE) {
      officeId = userAttributes.office?.id;
    }

    this.dialogService.showProgress();
    forkJoin([
      this.commonObjectService.getAgencies(officeId),
      this.commonObjectService.getChannelFamilies(ChannelFamily.USAGE_STREAMING),
      this.commonObjectService.getCountries()
    ]).subscribe({
      next: (responses: RESTApplicationResponse[]) => {
        this.agencies = responses[0].data;
        this.channelFamilies = responses[1].data;
        this.countries = responses[2].data;
        this.dialogService.hideProgress();
      },
      error: (err: Error) => {
        this.dialogService.hideProgress();
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  private createRowGroup(): FormGroup 
  {
    return this.fb.group({
      vertical: [[], Validators.required],
      format: [null, Validators.required],
      countryRegion: [[], Validators.required],
      placementName: ['', Validators.required],
      file: [{ value: '', disabled: !(this.showHttpFileSelectors || this.showAsperaFileSelectors) }, [Validators.required, CustomValidators.filenameValidator()]],
      media: [null], // Store file object
      formatList: [[]] // Dynamic format list based on vertical selection
    });
  }

  get rows(): FormArray 
  {
    return this.creativityForm.get('rows') as FormArray;
  }

  onAddRow(): void 
  {
    const newRow = this.createRowGroup();
    this.rows.push(newRow);
    this.updateTableDataSource();
  }

  onDeleteRow(index: number): void 
  {
    this.rows.removeAt(index);
    this.updateTableDataSource();
  }

  private updateTableDataSource(): void 
  {
    this.tableDataSource = [...this.rows.controls];
  }

  onAddFiles(): void 
  {
    if( this.creativityForm.get('uploadMethod')?.value.id == 1 ) {
      this.multiFileSelectorHttp.openFilePicker();
    } else {
      this.multiFileSelectorAspera.multiple = true;
      this.multiFileSelectorAspera.openFilePicker();
    }
  }

  onSelectChannelFamilies(row: FormGroup, families: BaseObject[]): void 
  {
    if (families != null && families.length > 0) {
      this.commonObjectService.getCommercialTypesByFamily(families).subscribe({
        next: (response: RESTApplicationResponse) => {
          row.patchValue({ formatList: response.data });
        },
        error: (err: Error) => {
          this.dialogService.openErrorDialog(err.message);
        }
      });
    } else {
      // Clear format list if no families selected
      row.patchValue({ formatList: [], format: null });
    }
  }

  onChangeUploadMethod(method: BaseObject): void 
  {
    if (method.id != AdGateConstants.UPLOADMETHOD_NOUPLOAD) {
      this.showAddFilesButton = true;
      // this.creativityForm.get('file')?.enable();
      this.toggleFileSelectorFormControls(true);

      if( method.id == AdGateConstants.UPLOADMETHOD_HTTP ) {
        this.showHttpFileSelectors = true;
        this.showAsperaFileSelectors = false;
      } else if( method.id == AdGateConstants.UPLOADMETHOD_ASPERA ) {
        this.showHttpFileSelectors = false;
        this.showAsperaFileSelectors = true;
      }
    } else {
      this.showAddFilesButton = false;
      this.showHttpFileSelectors = false;
      this.showAsperaFileSelectors = false;
      this.toggleFileSelectorFormControls(false);
    }
  }

  toggleFileSelectorFormControls( enable: boolean )
  {
    this.rows.controls.forEach(r => {
      if( enable ) {
        r.get('file')?.enable();
      } else {
        r.get('file')?.disable();
      }
    });
  }

  onSelectMultipleFilesHttp(selectedFiles: File[])
  {
    selectedFiles.forEach(f => {
      const newRow = this.createRowGroup();
      newRow.patchValue({ file: f.name, media: f });
      this.rows.push(newRow);
    })

    this.updateTableDataSource();
  }

  onSelectMultipleFilesAspera(selectedFiles: any)
  {
    (selectedFiles.dataTransfer.files as any[]).forEach(file => {
      const newRow = this.createRowGroup();
      newRow.patchValue({ file: StringUtils.filterFilename(file.name), media: { files: [file]} });
      this.rows.push(newRow);
    });

    this.updateTableDataSource();
  }

  onSelectFileHttp(selectedFiles: File[], row: FormGroup): void 
  {
    if (selectedFiles != null && selectedFiles.length > 0) {
      row.get('media')?.setValue(selectedFiles[0]);
    }
  }

  onSelectFileAspera(selectedFiles: any, row: FormGroup): void 
  {
    if (selectedFiles != null && selectedFiles.dataTransfer.files.length > 0) {
      row.get('media')?.setValue(selectedFiles.dataTransfer);
    }
  }

  onCancel(): void 
  {
    this.dialogRef.close(null);
  }

  onSubmit(): void 
  {
    // Mark all controls as touched to show validation errors
    this.creativityForm.markAllAsTouched();

    if (this.creativityForm.valid) {
      // Check if at least one row exists
      if (this.rows.length === 0) {
        this.dialogService.openErrorDialog('Please add at least one creativity.');
        return;
      }

      // Get form values (getRawValue includes disabled controls)
      const formValue = this.creativityForm.getRawValue();

      // Prepare result
      const result: StreamingCreativityAddDialogResult = new StreamingCreativityAddDialogResult();
      result.uploadMethod = formValue.uploadMethod;

      for( let r of formValue.rows ) {
        let c: Creativity = new Creativity();
        c.campaignId = this.campaign.id;
        c.placementName = r.placementName;
        c.channelFamilies = r.vertical;
        c.countries = r.countryRegion;
        c.type = r.format;

        let cr: CreateCreativityRequest = {
          creativity: c,
          media: r.media
        };

        result.createCreativityRequests.push(cr);
      }

      this.dialogRef.close(result);
    }
  }
}

export class StreamingCreativityAddDialogResult 
{
  uploadMethod!: BaseObject;
  createCreativityRequests: CreateCreativityRequest[] = [];
}
